import cn from 'classnames';
import { Fragment, useId } from 'react';
import ShowSchedule from 'de-sxm-reactlib-2/components/molecules/ShowSchedule/ShowSchedule';
import styles from './host.module.scss';
import { HostChannel } from '@/services/cache/ChannelCache';
import SXMLink from '@/shared-components/SXMLink';
import ShowMore from 'de-sxm-reactlib-2/components/molecules/ShowMore/ShowMore';
import SocialMediaIconLinks from 'de-sxm-reactlib-2/components/molecules/SocialMediaIconLinks/SocialMediaIconLinks';
import { SocialMediaIconLinksProps } from 'de-sxm-reactlib-2/components/molecules/SocialMediaIconLinks';
import ImageWrapper from '@/shared-components/ImageWrapper/ImageWrapper';

interface HostProps {
    className: string;
    name: string;
    description: React.ReactNode | React.ReactNode[];
    image: ImageProps;
    shows: SiteChannelShow[];
    socialMedia: SocialMediaIconLinksProps;
    channels: HostChannel[];
}

export function Host({
    className,
    name,
    description,
    image,
    shows,
    socialMedia,
    channels,
}: HostProps): React.JSX.Element {
    const contentId = useId();
    const alsoOn: React.ReactNode[] = [];

    channels?.forEach((channel, i) => {
        if (channel.pageUrl) {
            alsoOn.push(
                <Fragment key={i}>
                    <SXMLink href={channel.pageUrl} key={i} aria-label={channel.displayName}>
                        {channel.displayName}
                    </SXMLink>
                    &nbsp;
                </Fragment>,
            );
        } else {
            alsoOn.push(<Fragment key={i}>{channel.displayName}&nbsp;</Fragment>);
        }
    });

    return (
        <div className={cn(className, styles.item)}>
            <div className={styles.imageContainer}>
                {image.source && (
                    <ImageWrapper
                        optimizedWidth={100}
                        optimizedHeight={100}
                        className={styles.image}
                        src={image.source}
                        alt={image.altText}
                    />
                )}
            </div>
            <ShowMore
                className={styles.showMore}
                color={'var(--black)'}
                showText="View more"
                hideText="View less"
                contentClassName="flex flex-column"
                shownClassName={styles.showHideables}
                id={contentId}
            >
                <>
                    <p className={cn('bold', styles.name)}>{name}</p>

                    <div className={styles.info}>
                        <div className={cn(styles.infoLeft)}>
                            <div className="small">{description}</div>
                            {alsoOn.length > 0 && <p className="small">Also appears on {alsoOn}</p>}

                            <div className={cn(styles.hideable, styles.socialMedia)}>
                                <SocialMediaIconLinks
                                    {...socialMedia}
                                    className={cn(socialMedia.className, styles.socialMediaIcons)}
                                />
                            </div>
                        </div>

                        <div className={cn(styles.hideable, styles.infoRight)}>
                            <ShowSchedule displayTitles shows={shows} />
                        </div>
                    </div>
                </>
            </ShowMore>
        </div>
    );
}
